import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectById } from './services/projectService';
import Loader from './components/Loader';
import './project.css';
import './animations.css';
import useScrollAnimation from './hooks/useScrollAnimation';
import useParallax from './hooks/useParallax';
import { logProjectView } from './services/analyticsService';
import { resolveContentBlocks } from './utils/contentBlocks';

export default function Project(){
  // Modal for viewing gallery images
  const [modalImage, setModalImage] = useState(null);

  const modalStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.85)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };
  const closeBtnStyle = {
    position: "absolute",
    top: 16,
    right: 16,
    fontSize: 28,
    color: "#fff",
    background: "rgba(0,0,0,0.5)",
    border: "none",
    borderRadius: "50%",
    width: 44,
    height: 44,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
    "@media (max-width: 768px)": {
      top: 12,
      right: 12,
      width: 40,
      height: 40,
      fontSize: 24
    }
  };

  const renderModal = () => {
    const isMobile = window.innerWidth <= 768;
    return modalImage && (
      <div style={modalStyle} onClick={() => setModalImage(null)}>
        <button 
          style={{
            ...closeBtnStyle,
            top: isMobile ? 12 : 16,
            right: isMobile ? 12 : 16,
            width: isMobile ? 40 : 44,
            height: isMobile ? 40 : 44,
            fontSize: isMobile ? 24 : 28
          }} 
          onClick={e => { e.stopPropagation(); setModalImage(null); }} 
          aria-label="Close image">
          &times;
        </button>
        <img 
          src={modalImage} 
          alt="Full View" 
          style={{ 
            maxWidth: isMobile ? "95vw" : "90vw", 
            maxHeight: isMobile ? "90vh" : "85vh", 
            borderRadius: 12, 
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)" 
          }} 
          onClick={e => e.stopPropagation()} 
        />
      </div>
    );
  };
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otherProjects, setOtherProjects] = useState([]);
  const [heroVisible, setHeroVisible] = useState(false);
  const [detailsRef, detailsVisible] = useScrollAnimation(0.2);
  const [galleryRef, galleryVisible] = useScrollAnimation(0.3);
  const [testimonialRef, testimonialVisible] = useScrollAnimation(0.3);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.4);
  
  // Parallax effect for hero image
  const [heroParallaxRef, heroParallaxStyle] = useParallax({ strength: 0.2, max: 20 });
  
  // Parallax effect for gallery images
  const [galleryParallaxRef, galleryParallaxStyle] = useParallax({ strength: 0.15, max: 15 });

  // Make hero visible immediately after component loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroVisible(true);
    }, 100); // Very fast initial animation
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    async function loadProject() {
      try {
        setLoading(true);
        
        // Load from Firebase
        const firebaseProject = await getProjectById(slug);
        if (firebaseProject) {
          setProject({
            title: firebaseProject.name,
            type: `[${firebaseProject.type.toUpperCase()}]`,
            img: firebaseProject.mainImage,
            description: firebaseProject.description,
            client: firebaseProject.client,
            location: firebaseProject.location,
            team: firebaseProject.team,
            year: firebaseProject.year,
            awards: firebaseProject.awards,
            spacing: firebaseProject.spacing,
            status: firebaseProject.status || 'Completed',
            contentBlocks: firebaseProject.contentBlocks || [],
            imagesPreview: firebaseProject.images || [],
            testimonial: firebaseProject.testimonial ? {
              text: firebaseProject.testimonial.text,
              author: firebaseProject.testimonial.author
            } : null
          });
          // Track project view
          logProjectView(firebaseProject.name, firebaseProject.type);
        }
        setOtherProjects([]);
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    }

    loadProject();
  }, [slug]);

  if (loading) {
    return <Loader type="projects" />;
  }

  const renderImageThumb = (imageSrc, imageIndex) => {
    if (!imageSrc) return null;

    return (
      <div
        className={`thumb ${galleryVisible ? 'project-image-animate visible' : ''}`}
        style={{cursor: 'pointer', width: '100%'}}
        onClick={() => setModalImage(imageSrc)}
      >
        <img src={imageSrc} alt={`${project.title || project.name} ${imageIndex + 1}`} style={{boxShadow: 'none'}} />
      </div>
    );
  };

  const getBlockSequence = (block) => {
    if (block?.sequence === 'before-details' || block?.sequence === 'after-details' || block?.sequence === 'after-images') {
      return block.sequence;
    }

    return null;
  };

  const renderDetailsSection = () => (
    <div className="btw-alt-row btw-alt-text-row btw-alt-row-single">
      <div className="btw-alt-col btw-alt-text" ref={detailsRef}>
        <div className={detailsVisible ? 'btw-project-details-label subtitle-animate visible' : 'btw-project-details-label'} style={{marginBottom: '12px', fontSize: '0.8rem'}}>
          Project Details
        </div>
        {project.description && (
          <div className={detailsVisible ? 'btw-project-details-desc description-animate visible' : 'btw-project-details-desc'} style={{ color: '#181616', whiteSpace: 'pre-wrap', maxWidth: '760px' }}>
            {project.description}
          </div>
        )}
      </div>
    </div>
  );

  const renderGallerySection = (images = project.imagesPreview || []) => (
    images.length > 0 && (
      <div className="btw-extra-images-grid">
        {images.map((imageSrc, idx) => (
          <div key={idx}>
            {renderImageThumb(imageSrc, idx)}
          </div>
        ))}
      </div>
    )
  );

  const renderDetailsAndGallery = () => {
    const images = project.imagesPreview || [];

    return (
      <>
        {renderDetailsSection()}
        {renderGallerySection(images)}
      </>
    );
  };

  const renderTextColumn = (text, showLabel = false, key) => (
    <div className="btw-alt-col btw-alt-text" ref={showLabel ? detailsRef : undefined} key={key}>
      {showLabel && (
        <div className={detailsVisible ? 'btw-project-details-label subtitle-animate visible' : 'btw-project-details-label'} style={{marginBottom: '12px', fontSize: '0.8rem'}}>
          Project Details
        </div>
      )}
      <div className={detailsVisible ? 'btw-project-details-desc description-animate visible' : 'btw-project-details-desc'} style={{ color: '#181616', whiteSpace: 'pre-wrap', maxWidth: '760px' }}>
        {text}
      </div>
    </div>
  );

  const renderImageTextRow = (block, key, showLabel = false) => {
    const textColumn = renderTextColumn(block.text || '', showLabel, `${key}-text`);
    const imageColumn = (
      <div className="btw-alt-col btw-alt-img" key={`${key}-image`}>
        {renderImageThumb(block.imageSrc, block.imageIndex || 0)}
      </div>
    );

    if (block.placement === 'image-left') {
      return (
        <div className="btw-alt-row btw-alt-text-row btw-image-text-row btw-image-text-row-image-left" key={key}>
          {imageColumn}
          {textColumn}
        </div>
      );
    }

    if (block.placement === 'stacked') {
      return (
        <div className="btw-alt-row btw-alt-text-row btw-alt-row-single btw-image-text-row btw-image-text-row-stacked" key={key}>
          {textColumn}
          {imageColumn}
        </div>
      );
    }

    return (
      <div className="btw-alt-row btw-alt-text-row btw-image-text-row btw-image-text-row-text-left" key={key}>
        {textColumn}
        {imageColumn}
      </div>
    );
  };

  const renderSequencedBlock = (block, key) => {
    if (block.type === 'text') {
      return (
        <div className="btw-alt-row btw-alt-text-row btw-alt-row-single" key={key}>
          {renderTextColumn(block.text || '', false)}
        </div>
      );
    }

    if (block.type === 'image') {
      return (
        <div className="btw-alt-row btw-alt-text-row btw-alt-row-single" key={key}>
          <div className="btw-alt-col btw-alt-img">
            {renderImageThumb(block.imageSrc, block.imageIndex || 0)}
          </div>
        </div>
      );
    }

    if (block.type === 'imageText') {
      return renderImageTextRow(block, key, false);
    }

    if (block.type === 'gallery') {
      return (
        <div className="btw-extra-images-grid" key={key}>
          {block.galleryImages.map((item) => (
            <div key={`${key}-${item.index}`}>
              {renderImageThumb(item.src, item.index)}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderContentBlocks = () => {
    const resolvedBlocks = resolveContentBlocks(project.contentBlocks, project.imagesPreview || []);

    if (!resolvedBlocks.length) {
      return renderDetailsAndGallery();
    }

    const hasSequencedBlocks = resolvedBlocks.length > 0;

    if (hasSequencedBlocks) {
      const beforeDetailsBlocks = [];
      const afterDetailsBlocks = [];
      const afterImagesBlocks = [];
      const images = project.imagesPreview || [];

      resolvedBlocks.forEach((block, index) => {
        const sequence = getBlockSequence(block) || 'after-details';
        const key = `${block.id || `${block.type}-${index}`}-sequenced-${index}`;

        if (sequence === 'before-details') {
          beforeDetailsBlocks.push({ block, key });
          return;
        }

        if (sequence === 'after-images') {
          afterImagesBlocks.push({ block, key });
          return;
        }

        afterDetailsBlocks.push({ block, key });
      });

      return (
        <>
          {beforeDetailsBlocks.map(({ block, key }) => renderSequencedBlock(block, key))}
          {renderDetailsSection()}
          {afterDetailsBlocks.map(({ block, key }) => renderSequencedBlock(block, key))}
          {renderGallerySection(images)}
          {afterImagesBlocks.map(({ block, key }) => renderSequencedBlock(block, key))}
        </>
      );
    }

    let detailsLabelShown = false;

    return (
      <>
        {resolvedBlocks.map((block, index) => {
          const key = block.id || `${block.type}-${index}`;

          if (block.type === 'text') {
            const showLabel = !detailsLabelShown;
            if (showLabel) detailsLabelShown = true;

            return (
              <div className="btw-alt-row btw-alt-text-row btw-alt-row-single" key={key}>
                {renderTextColumn(block.text || '', showLabel)}
              </div>
            );
          }

          if (block.type === 'image') {
            return (
              <div className="btw-alt-row btw-alt-text-row btw-alt-row-single" key={key}>
                <div className="btw-alt-col btw-alt-img">
                  {renderImageThumb(block.imageSrc, block.imageIndex || 0)}
                </div>
              </div>
            );
          }

          if (block.type === 'imageText') {
            const showLabel = !detailsLabelShown;
            if (showLabel) detailsLabelShown = true;

            return renderImageTextRow(block, key, showLabel);
          }

          if (block.type === 'gallery') {
            return (
              <div className="btw-extra-images-grid" key={key}>
                {block.galleryImages.map((item) => (
                  <div key={`${key}-${item.index}`}>
                    {renderImageThumb(item.src, item.index)}
                  </div>
                ))}
              </div>
            );
          }

          return null;
        })}
      </>
    );
  };

  if(!project){
    return (
      <main style={{padding: '80px 24px', color: '#f7f5ec'}}>
        <div style={{maxWidth: 960, margin: '0 auto', textAlign: 'center'}}>
          <h2>Project not found</h2>
          <p>We couldn't find that project. Try browsing the projects list.</p>
          <Link to="/" style={{color: '#D6D0C3'}}>Back to home</Link>
        </div>
      </main>
    );
  }

  const hasDetailImages = Array.isArray(project.imagesPreview) && project.imagesPreview.length > 0;
  const hasContentBlocks = Array.isArray(project.contentBlocks) && project.contentBlocks.length > 0;

  return (
    <main style={{background: '#fff', minHeight: '100vh', color: '#f7f5ec'}}>
      <div style={{width: '100%', background: '#fff'}}>
        {/* Project name and details in a container above hero image */}
        <div style={{width: '100%', display: 'flex', justifyContent: 'center', padding: '20px'}}>
          <div style={{width: '94vw', maxWidth: 1280, margin: '40px auto 0'}}>
            <div className="btw-project-top-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%'}}>
              <div className="btw-project-title-wrap" style={{textAlign: 'left', flex: '1 1 auto', minWidth: 0}}>
                <h2 className="btw-project-name" style={{color: '#baa170', fontFamily: 'instrument serif, serif', fontWeight: 300, fontStyle: 'normal', fontSize: '2.4rem', letterSpacing: '-1px', margin: 0, overflowWrap: 'anywhere', wordBreak: 'break-word'}}>{(project.title || project.name).toUpperCase()}</h2>
              </div>
              <div className="btw-project-meta-col" style={{textAlign: 'left', color: '#baa170', minWidth: 320, borderLeft: '1.5px solid #baa170', paddingLeft: '25px',}}>
                {project.client && (
                  <div className="btw-project-meta-row" style={{fontSize: '1.0rem'}}>
                    <span style={{color: '#baa170'}}>Client </span>  <span style={{fontWeight: 600}}>{project.client}</span>
                  </div>
                )}
                {project.location && (
                  <div className="btw-project-meta-row" style={{fontSize: '1.0rem'}}>
                    <span style={{color: '#baa170'}}>Location </span>  <span style={{fontWeight: 600}}>{project.location}</span>
                  </div>
                )}
                {project.year && (
                  <div className="btw-project-meta-row" style={{fontSize: '1.0rem'}}>
                    <span style={{color: '#baa170'}}>Year </span>  <span style={{fontWeight: 600}}>{project.year}</span>
                  </div>
                )}
                {project.team && (
                  <div className="btw-project-meta-row" style={{fontSize: '1.0rem'}}>
                    <span style={{color: '#baa170'}}>Team </span>  <span style={{fontWeight: 600}}>{project.team}</span>
                  </div>
                )}
                {project.spacing && (
                  <div className="btw-project-meta-row" style={{fontSize: '1.0rem'}}>
                    <span style={{color: '#baa170'}}>Spacing </span>  <span style={{fontWeight: 600}}>{project.spacing}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Hero image across full width of content area */}
        <div style={{width: '94vw', maxWidth: 1280, margin: '24px auto 0', paddingLeft: 20, paddingRight: 20, boxSizing: 'border-box'}}>
          <div className="btw-project-image btw-project-hero" style={{overflow: 'hidden', borderRadius: 6, position: 'relative', width: '100%'}}>
            {project.img ? (
              <div ref={heroParallaxRef} style={heroParallaxStyle}>
                <img 
                  className={`fade-in-scale ${heroVisible ? 'visible' : ''}`} 
                  src={project.img} 
                  alt={project.title || project.name} 
                  style={{
                    transition: 'opacity 0.3s ease-in-out',
                    width: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ) : (
              <div className="btw-project-hero-fallback">
                No main image available
              </div>
            )}
          </div>
        </div>


        {/* Content blocks when configured, otherwise full details text + image gallery */}
        {hasContentBlocks || hasDetailImages ? (
          <div className="btw-alt-grid" ref={galleryRef} style={{width: '94vw', maxWidth: 1280, margin: '50px auto 50px auto', padding: '0 40px', boxSizing: 'border-box'}}>
            {hasContentBlocks ? renderContentBlocks() : renderDetailsAndGallery()}
            {/* Awards section if available */}
            {project.awards && (
              <div style={{marginTop: '48px', gridColumn: '1 / -1'}}>
                <div className="btw-project-details-meta">
                  <div className={detailsVisible ? 'btw-project-details-meta-row fade-in-up visible' : 'btw-project-details-meta-row'}>
                    AWARDS: <span style={{fontWeight: 500}}>{project.awards}</span>
                  </div>
                </div>
              </div>
            )}
            {/* Testimonial section - aligned with images like project details */}
            {project.testimonial && (
              <div className="btw-alt-row btw-testimonial-row">
                <div className="btw-alt-col btw-alt-text" ref={testimonialRef}>
                  <div className={`btw-project-details-label subtitle-animate ${testimonialVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>Testimonial</div>
                  <div className={`btw-project-details-desc description-animate ${testimonialVisible ? 'visible' : ''}`} style={{ color: '#181616', whiteSpace: 'pre-wrap' }}>
                    {project.testimonial.text}
                  </div>
                  <div className={`btw-after-gallery-author fade-in-up ${testimonialVisible ? 'visible' : ''}`}>
                    {project.testimonial.author}
                  </div>
                </div>
                <div className="btw-alt-col btw-alt-img"></div>
              </div>
            )}
            {renderModal()}
          </div>
        ) : (
          // fallback: original details, then gallery, then testimonial for projects without images
          <>
            <div className="btw-project-details-responsive" ref={detailsRef}>
              <div className="btw-project-details-flex">
                <div className={detailsVisible ? 'btw-project-details-label subtitle-animate visible' : 'btw-project-details-label'}>[PROJECT DETAILS]</div>
                <div className="btw-project-details-content">
                  <div className={detailsVisible ? 'btw-project-details-desc description-animate visible' : 'btw-project-details-desc'} style={{ maxWidth: '760px' }}>
                    {project.description}
                  </div>
                  <div className="btw-project-details-meta">
                    {project.client && (
                      <div className={detailsVisible ? 'btw-project-details-meta-row fade-in-up visible' : 'btw-project-details-meta-row'}>
                        CLIENT {project.client}
                      </div>
                    )}
                    {project.location && (
                      <div className={detailsVisible ? 'btw-project-details-meta-row fade-in-up visible' : 'btw-project-details-meta-row'}>
                        LOCATION <span style={{fontWeight: 500}}>{project.location}</span>
                      </div>
                    )}
                    {project.year && (
                      <div className={detailsVisible ? 'btw-project-details-meta-row fade-in-up visible' : 'btw-project-details-meta-row'}>
                        YEAR <span style={{fontWeight: 500}}>{project.year}</span>
                      </div>
                    )}
                    {project.team && (
                      <div className={detailsVisible ? 'btw-project-details-meta-row fade-in-up visible' : 'btw-project-details-meta-row'}>
                        TEAM <span style={{fontWeight: 500}}>{project.team}</span>
                      </div>
                    )}
                    {project.awards && (
                      <div className={detailsVisible ? 'btw-project-details-meta-row fade-in-up visible' : 'btw-project-details-meta-row'}>
                        AWARDS <span style={{fontWeight: 500}}>{project.awards}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Other projects gallery - only for dummy projects without additional images */}
            {(!project.imagesPreview || project.imagesPreview.length === 0) && otherProjects.length > 0 && (
              <div style={{width: '94vw', maxWidth: 1280, margin: '0 auto 0px'}} ref={galleryRef}>
                <div className="btw-project-gallery" ref={galleryParallaxRef} style={galleryParallaxStyle}>
                  {otherProjects.map((otherProject, i) => (
                    <div className={`thumb ${galleryVisible ? 'project-image-animate visible' : ''}`} key={i} style={{transitionDelay: `${i * 0.1}s`}}>
                      <img 
                        src={otherProject.img || otherProject.mainImagePreview} 
                        alt={otherProject.title || otherProject.name} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Testimonial section (only for projects without images) */}
            {(!project.imagesPreview || project.imagesPreview.length === 0) && project.testimonial && (
              <div className="btw-after-gallery-responsive" ref={testimonialRef}>
                <div className="btw-after-gallery-flex">
                  <div className={`btw-after-gallery-label subtitle-animate ${testimonialVisible ? 'visible' : ''}`}>[TESTIMONIAL]</div>
                  <div className="btw-after-gallery-content">
                    <div className={`btw-after-gallery-text description-animate ${testimonialVisible ? 'visible' : ''}`}>{project.testimonial.text}</div>
                    <div className={`btw-after-gallery-author fade-in-up ${testimonialVisible ? 'visible' : ''}`}>{project.testimonial.author}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </div>

      {/* Get Started section */}
      <section className="btw-getstarted-section bg-neutral-900 min-h-[60vh] flex flex-col justify-center items-center text-center" ref={ctaRef}>
        <div className="btw-getstarted-inner">
          <div className={`text-neutral-400 font-mono mb-6 subtitle-animate ${ctaVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>GET  STARTED</div>
          <h1 className={`text-5xl md:text-7xl font-bold text-[#181816] mb-8 leading-tight hero-title-animate ${ctaVisible ? 'visible' : ''}`}>TRANSFORM YOUR<br />VISION INTO REALITY</h1>
          <Link to="/letstalk" style={{textDecoration: 'none'}}>
            <button className={`btw-cta fade-in-up ${ctaVisible ? 'visible' : ''}`} type="button"><span>LET'S TALK</span></button>
          </Link>
        </div>
      </section>
    </main>
  );
}
